import type { SupportedCountry } from '@archipro-website/localisation';
import type { HeaderNavLinks } from '@rocco/ui/website-header/components/Header';
import { getDomain } from './get-domain';

// eslint-disable-next-line no-restricted-syntax
export enum PagesEnum {
    PROJECTS = '/projects',
    PRODUCTS = '/products',
    PROFESSIONALS = '/professionals',
    ROOMS = '/rooms',
    ARCHIZEEN = '/articles',
    DIGITAL_MAGAZINES = '/digital-magazines',
    ABOUT = '/about-us',
    CONTACT = '/contact-us',
    HOME_DESIGN_EVENING = '/home-design-event',
    INSTAGRAM = 'https://www.instagram.com/archipro_/',
    FACEBOOK = 'https://www.facebook.com/archipro/',
    LINKEDIN = 'https://www.linkedin.com/company/archipro',
    YOUTUBE = 'https://www.youtube.com/channel/UCw3LUhWr0j-z2rpqGfQK8Tg',
    HOW_IT_WORKS = '/how-it-works',
    /**
     * US alternative opt-out link target. Surfaced in the footer for the US
     * market only; the page itself is reachable in every market.
     */
    YOUR_PRIVACY_CHOICES = '/your-privacy-choices',
    // Member-related routes
    MY_GUIDES = '/app/guides',
    MY_PROJECTS = '/app/my-projects',
    /** Canonical path for member design boards. */
    MY_BOARDS = '/app/boards',
    MY_TICKETS = '/app/my-tickets',
    /** @deprecated Use MY_BOARDS. Kept for compatibility/redirects. */
    MY_MOOD_BOARDS = '/app/my-mood-boards',
    MY_DESIGN_BOARDS = '/member/design-boards',
    MY_ORDERS = '/member/orders',
    GET_MATCHED_REQUESTS = '/member/get-matched-requests',
    GET_MATCHED_REQUESTS_ADMIN = '/member/get-matched-requests-admin',
    MY_NOTIFICATIONS = '/member/notifications',
    MY_SETTINGS = '/member/settings/edit-profile',
    LEGACY_INBOX = '/member/inbox',
    INBOX = '/app/inbox',
    LIST_BUSINESS = '/advertising',
    // Business site advertise / resources routes
    CLIENT_MARKETING = 'https://business.{AP_DOMAIN_PLACE}/advertise/client-marketing',
    CUSTOMER_STORIES = 'https://business.{AP_DOMAIN_PLACE}/resources/customer-stories',
    LIVE_EVENTS = 'https://business.{AP_DOMAIN_PLACE}/advertise/advertise-hde',
    // Business site why archipro routes
    HOW_ARCHIPRO_WORKS = 'https://business.{AP_DOMAIN_PLACE}/why-archipro/how-archipro-works',
    THE_ARCHIPRO_AUDIENCE = 'https://business.{AP_DOMAIN_PLACE}/why-archipro/the-archipro-audience',
    GET_STARTED = 'https://business.{AP_DOMAIN_PLACE}/why-archipro/get-started',
    // Business solutions routes
    BECOME_A_PRO = 'https://business.{AP_DOMAIN_PLACE}',
    INSIGHTS = 'https://business.{AP_DOMAIN_PLACE}/solutions/insights',
    ECOMMERCE = 'https://business.{AP_DOMAIN_PLACE}/solutions/ecommerce',
    CONTENT = 'https://business.{AP_DOMAIN_PLACE}/solutions/content',
    SOFTWARE_TOOLS = 'https://business.{AP_DOMAIN_PLACE}/solutions/software-tools',
    FOR_PROFESSIONALS = 'https://business.{AP_DOMAIN_PLACE}/for-professionals',
    FOR_SUPPLIERS_AND_BRANDS = 'https://business.{AP_DOMAIN_PLACE}/for-suppliers',
    // Business site membership routes
    MEMBERSHIP_PROFESSIONALS = 'https://business.{AP_DOMAIN_PLACE}/memberships/for-professionals',
    MEMBERSHIP_SUPPLIERS = 'https://business.{AP_DOMAIN_PLACE}/memberships/for-suppliers',
}

export const FOOTER_WHY_ARCHIPRO_LINKS = {
    title: 'Why ArchiPro?',
    links: [
        {
            title: 'How ArchiPro Works',
            to: PagesEnum.HOW_ARCHIPRO_WORKS,
            newTab: true,
        },
        {
            title: 'The ArchiPro Audience',
            to: PagesEnum.THE_ARCHIPRO_AUDIENCE,
            newTab: true,
        },
        {
            title: 'Get Started',
            to: PagesEnum.GET_STARTED,
            newTab: true,
        },
    ],
};

export const FOOTER_SOLUTIONS_LINKS = {
    title: 'Business Solutions',
    links: [
        {
            title: 'Become a Pro',
            to: PagesEnum.BECOME_A_PRO,
            newTab: true,
        },
        {
            title: 'Audience Insights',
            to: PagesEnum.INSIGHTS,
            newTab: true,
        },
        {
            title: 'Sell on ArchiPro',
            to: PagesEnum.ECOMMERCE,
            newTab: true,
        },
        {
            title: 'Digital Marketing',
            to: PagesEnum.CLIENT_MARKETING,
            newTab: true,
        },
        {
            title: 'Content Creation',
            to: PagesEnum.CONTENT,
            newTab: true,
        },
        {
            title: 'Software Tools',
            to: PagesEnum.SOFTWARE_TOOLS,
            newTab: true,
        },
        {
            title: 'For Professionals',
            to: PagesEnum.FOR_PROFESSIONALS,
            newTab: true,
        },
        {
            title: 'For Suppliers & Brands',
            to: PagesEnum.FOR_SUPPLIERS_AND_BRANDS,
            newTab: true,
        },
    ],
};

export const FOOTER_MEMBERSHIP_LINKS = {
    title: 'Memberships',
    links: [
        {
            title: 'For Professionals',
            to: PagesEnum.MEMBERSHIP_PROFESSIONALS,
            newTab: true,
        },
        {
            title: 'For Suppliers',
            to: PagesEnum.MEMBERSHIP_SUPPLIERS,
            newTab: true,
        },
    ],
};

export const FOOTER_RESOURCES_LINKS = {
    title: 'Resources',
    links: [
        {
            title: 'Customer Stories',
            to: PagesEnum.CUSTOMER_STORIES,
            newTab: true,
        },
        {
            title: 'Live Events',
            to: PagesEnum.LIVE_EVENTS,
            newTab: true,
        },
    ],
};

export const MOBILE_HEADER_PRIMARY_NAV = {
    title: 'MOBILE_HEADER_PRIMARY_NAV',
    links: [
        { title: 'Source Products', to: PagesEnum.PRODUCTS },
        { title: 'Explore Projects', to: PagesEnum.PROJECTS },
        { title: 'Hire Professionals', to: PagesEnum.PROFESSIONALS },
        { title: 'Read Articles', to: PagesEnum.ARCHIZEEN },
    ],
};

export const MOBILE_HEADER_SECONDARY_NAV = {
    title: 'MOBILE_HEADER_SECONDARY_NAV',
    links: [
        { title: 'Digital Magazines', to: PagesEnum.DIGITAL_MAGAZINES },
        { title: 'About us', to: PagesEnum.ABOUT },
        {
            title: 'Home Design Evening',
            to: PagesEnum.HOME_DESIGN_EVENING,
        },
    ],
};

export const PROFILE_NAV = {
    title: 'PROFILE_NAV',
    links: [
        {
            title: 'Personal Inbox',
            to: PagesEnum.LEGACY_INBOX,
            isNonRemixRoute: true,
        },
        { title: 'My Projects', to: PagesEnum.MY_PROJECTS },
        { title: 'My Design Boards', to: PagesEnum.MY_BOARDS },
        { title: 'My Event Tickets', to: PagesEnum.MY_TICKETS },
        { title: 'Guides', to: PagesEnum.MY_GUIDES },
        { title: 'Orders', to: PagesEnum.MY_ORDERS, isNonRemixRoute: true },
        {
            title: 'Get Matched Requests',
            to: PagesEnum.GET_MATCHED_REQUESTS,
            isNonRemixRoute: true,
        },
        {
            title: 'Get Matched (Admin)',
            to: PagesEnum.GET_MATCHED_REQUESTS_ADMIN,
            isNonRemixRoute: true,
            adminOnly: true,
        },
        {
            title: 'Notifications',
            to: PagesEnum.MY_NOTIFICATIONS,
            isNonRemixRoute: true,
        },
        {
            title: 'My Account',
            to: PagesEnum.MY_SETTINGS,
            isNonRemixRoute: true,
        },
        {
            title: 'List a Business',
            to: PagesEnum.LIST_BUSINESS,
            isNonRemixRoute: true,
        },
    ],
};

export const DESKTOP_NAV: HeaderNavLinks = {
    title: 'DESKTOP_NAV',
    links: [
        {
            prefix: 'Find',
            to: PagesEnum.PRODUCTS,
            title: 'Products',
            category: 'Products',
        },
        {
            prefix: 'Hire',
            to: PagesEnum.PROFESSIONALS,
            title: 'Professionals',
            category: 'Professionals',
        },
        {
            prefix: 'Explore',
            to: PagesEnum.PROJECTS,
            title: 'Projects',
            category: 'Projects',
        },
        {
            title: 'Rooms',
            to: PagesEnum.ROOMS,
            category: 'Rooms',
        },
        {
            prefix: 'Home',
            to: PagesEnum.HOME_DESIGN_EVENING,
            title: 'Home Design Evening',
            category: 'Home Design Evening',
            isNonRemixRoute: true,
        },
    ],
};

export const getPrivacyPolicyLink = (countryCode: SupportedCountry): string => {
    return `https://${getDomain(countryCode)}/privacy-policy`;
};

export const getWebsiteTermsLink = (countryCode: SupportedCountry): string => {
    return `https://${getDomain(countryCode)}/terms-of-use`;
};
